/* Ocean Racing sticky add-to-cart bar (ntd-sticky-atc.js), ported from the LTHR pattern 2026-10-06.
   Guide mode while the real Add to Cart button is on screen (or still below the fold): a tap scrolls to the
   variant picker and never adds to cart. Proxy mode once the real button has scrolled above the viewport: the
   bar mirrors the real button (label, disabled, loading) and a tap clicks it, so the variant always comes from
   Dawn's own form (name="id", kept current by product-info.js). The bar never stores a variant id. */
if (!customElements.get('ntd-sticky-atc')) {
  customElements.define(
    'ntd-sticky-atc',
    class NtdStickyAtc extends HTMLElement {
      connectedCallback() {
        this.submitProxy = this.querySelector('[data-ntd-sticky-submit]');
        this.priceEl = this.querySelector('[data-ntd-sticky-price]');
        this.labelEl = this.querySelector('[data-ntd-sticky-label]');
        this.variantEl = this.querySelector('[data-ntd-sticky-variant]');
        this.productInfo = document.querySelector('product-info[data-section]');
        this.sectionId = this.productInfo ? this.productInfo.dataset.section : null;
        const scope = this.productInfo || document;
        this.mainSubmit = scope.querySelector('.product-form__submit');
        this.picker = scope.querySelector('variant-selects');
        this.guideLabel = this.dataset.guideLabel || '';
        this.mode = null;
        if (!this.mainSubmit || !this.submitProxy) return;

        this.submitProxy.addEventListener('click', () => {
          if (this.mode === 'proxy') { this.mainSubmit.click(); return; }
          this.scrollToPicker();
        });

        /* rAF-throttled scroll check rather than IntersectionObserver: an anchor jump can cross the whole
           viewport in one frame and IO never fires for that. */
        this.updateMode = () => {
          if (this.rafId) return;
          this.rafId = requestAnimationFrame(() => { this.rafId = null; this.applyMode(); });
        };
        window.addEventListener('scroll', this.updateMode, { passive: true });
        window.addEventListener('resize', this.updateMode, { passive: true });

        document.body.classList.add('ntd-has-sticky-atc');
        if ('ResizeObserver' in window) {
          this.sizeObserver = new ResizeObserver(() => this.publishHeight());
          this.sizeObserver.observe(this);
        }

        /* The real button node persists across variant swaps; mirror its state from attribute mutations. */
        this.syncObserver = new MutationObserver(() => this.sync());
        this.syncObserver.observe(this.mainSubmit, { attributes: true, childList: true, subtree: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const id = event && event.data ? event.data.sectionId : undefined;
            if (!this.sectionId || !id || id === this.sectionId) { this.sync(); this.syncVariant(event); }
          });
        }

        this.applyMode();
        this.sync();
        this.publishHeight();
      }

      disconnectedCallback() {
        if (this.updateMode) { window.removeEventListener('scroll', this.updateMode); window.removeEventListener('resize', this.updateMode); }
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.syncObserver) this.syncObserver.disconnect();
        if (this.sizeObserver) this.sizeObserver.disconnect();
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        document.body.classList.remove('ntd-has-sticky-atc');
        document.documentElement.style.removeProperty('--ntd-sticky-atc-height');
      }

      /* Proxy once the real button's bottom edge is above the viewport; guide otherwise. */
      applyMode() {
        const rect = this.mainSubmit.getBoundingClientRect();
        this.setMode(rect.bottom < 0 ? 'proxy' : 'guide');
      }

      setMode(mode) {
        if (this.mode === mode) return;
        this.mode = mode;
        this.setAttribute('data-mode', mode);
        this.sync();
      }

      publishHeight() {
        const height = Math.round(this.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--ntd-sticky-atc-height', height + 'px');
      }

      scrollToPicker() {
        const target = this.picker || this.mainSubmit;
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.picker) {
          this.picker.classList.add('ntd-sticky-atc__picker-flash');
          setTimeout(() => this.picker.classList.remove('ntd-sticky-atc__picker-flash'), 1200);
        }
      }

      /* Label, disabled and loading mirror the real button in proxy mode; guide mode keeps the guide label. */
      sync() {
        if (!this.mainSubmit || !this.submitProxy) return;
        const scope = this.productInfo || document;
        const priceSource = scope.querySelector('.price__container .price-item--sale, .price__container .price-item--regular');
        if (this.priceEl && priceSource) this.priceEl.textContent = priceSource.textContent.trim();
        if (this.mode === 'proxy') {
          const labelNode = this.mainSubmit.querySelector('span:not(.loading__spinner)');
          const label = labelNode ? labelNode.textContent.trim() : this.mainSubmit.textContent.trim();
          if (this.labelEl && label) this.labelEl.textContent = label;
          this.submitProxy.disabled = this.mainSubmit.disabled;
          this.submitProxy.classList.toggle('loading', this.mainSubmit.classList.contains('loading'));
        } else {
          if (this.labelEl) this.labelEl.textContent = this.guideLabel;
          this.submitProxy.disabled = false;
          this.submitProxy.classList.remove('loading');
        }
      }

      syncVariant(event) {
        if (!this.variantEl) return;
        const variant = event && event.data ? event.data.variant : null;
        if (variant && variant.title && variant.title !== 'Default Title') {
          this.variantEl.textContent = variant.title;
          this.variantEl.hidden = false;
        }
      }
    }
  );
}
